import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import type { Fee } from '@/services/earnings/fees'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import type { Targeting } from '@/services/targeting/targeting'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface Creative {
  id: string
  name: string
  objectState: string
  /** The user-set state: NOT_READY, REQUIRES_REVIEW, APPROVED, REJECTED or PAUSED. */
  userState: string
  /** Why review rejected the creative, e.g. POOR_QUALITY. Only set when REJECTED. */
  rejectedReason?: string
  /** Owning network — only populated by list queries that span networks. */
  advertiserId: string
  advertiserName: string
  lineItemId: string
  lineItemName: string
  width: number
  height: number
  type: string
  layoutId: string
  layoutName: string
  layoutParameters: Record<string, string | { id: string } | null>
  /** Targets narrowing where this one creative may serve, within its line item's targeting. */
  targeting: Targeting
  /**
   * Whether `targeting` is the creative's own, so a list can show it without
   * fetching the creative — false for concise lists, which only count targets.
   */
  targetingComplete: boolean
  /**
   * Share of voice: the percentage (0–100] of its line item's impressions this
   * creative takes. Absent when the line item rotates its creatives itself.
   */
  sponsorshipPercentage?: number
  /**
   * Per-impression fee the creative's layout charges this advertiser, set by
   * the server from the advertiser's or team's layout fees. Absent from concise
   * list responses.
   */
  creativeFormatFee?: Fee
  /** Third-party URLs the ad server calls each time the creative is shown. */
  impressionTrackingUrls: string[]
  /** True when a validation warning stops the creative from serving at all. */
  blocksAdServing: boolean
  updateTime: string
  createTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

export interface CreativeInput {
  name: string
  objectState?: string
  userState?: string
  advertiser?: string
  lineItem?: string
  layout?: string
  layoutParameters?: Record<string, string | { id: string } | null>
  targeting?: Targeting
  width?: number
  height?: number
  /** `null` clears the share of voice. */
  sponsorshipPercentage?: number | null
  /** Replaces the whole list; an empty array removes every tracker. */
  impressionTrackingUrls?: string[]
}

/** One third-party tag read out of an ad server's trafficking sheet. */
export interface ThirdPartyTagRow {
  name: string
  tag: string
  width?: number
  height?: number
}

/** One creative of a bulk save: the id, and only the fields being changed. */
export type CreativeBulkInput = Partial<CreativeInput> & { id: string }

function mapCreative(r: Record<string, unknown>, concise = false): Creative {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    userState: (r.userState as string) ?? '',
    rejectedReason: (r.rejectedReason as string | undefined) ?? undefined,
    advertiserId: ((r.advertiser as Record<string, unknown>)?.id as string) ?? '',
    advertiserName: ((r.advertiser as Record<string, unknown>)?.name as string) ?? '',
    lineItemId: ((r.lineItem as Record<string, unknown>)?.id as string) ?? '',
    lineItemName: ((r.lineItem as Record<string, unknown>)?.name as string) ?? '',
    width: (r.width as number) ?? 0,
    height: (r.height as number) ?? 0,
    type: (r.type as string) ?? '',
    layoutId: ((r.layout as Record<string, unknown>)?.id as string) ?? '',
    layoutName: ((r.layout as Record<string, unknown>)?.name as string) ?? '',
    layoutParameters: (r.layoutParameters as Record<string, string | { id: string } | null>) ?? {},
    targeting: (r.targeting as Targeting) ?? {},
    targetingComplete: !concise,
    sponsorshipPercentage: (r.sponsorshipPercentage as number | undefined) ?? undefined,
    creativeFormatFee: (r.creativeFormatFee as Fee | undefined) ?? undefined,
    impressionTrackingUrls: (r.impressionTrackingUrls as string[] | undefined) ?? [],
    blocksAdServing: ((r.validationWarnings as Record<string, unknown>[]) ?? []).some(
      (w) => w.preventsAdServing === true
    ),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
    createTime: (r.createTime as string) ?? '',
  }
}

export interface CreativeListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of creatives, narrowed by the given filters on the server. */
async function fetchCreativePage(
  { search, status, sort }: CreativeListOptions,
  page: number,
  pageSize: number
): Promise<ListPage<Creative>> {
  const params = new URLSearchParams({
    ...searchParams('creatives', search),
    context: getContext(),
    concise: 'true',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/creatives?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch creatives')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map((r) => mapCreative(r, true))
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const CreativesApi = {
  getPage: ({ page = 1, pageSize = 25, ...filters }: CreativeListOptions = {}) =>
    fetchCreativePage(filters, page, pageSize),

  getByAdvertiser: async (advertiserId: string): Promise<Creative[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      advertiser: advertiserId,
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/creatives?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch creatives (${res.status})`)
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? data
    return results.map((r) => mapCreative(r))
  },

  /**
   * The line item's code, which addresses its transparency notice. The API
   * only puts it on creatives, so it is read off any one of them — null when
   * the line item has none yet.
   */
  getLineItemCode: async (lineItemId: string): Promise<string | null> => {
    const params = new URLSearchParams({
      context: getContext(),
      lineItem: lineItemId,
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '1',
      page: '1',
    })
    const res = await apiFetch(`${API_BASE}/creatives?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch creatives (${res.status})`)
    const data = await res.json()
    const first = ((data.results ?? data) as Record<string, unknown>[])[0]
    return (first?.lineItemCode as string) || null
  },

  getByLineItem: async (lineItemId: string): Promise<Creative[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      lineItem: lineItemId,
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/creatives?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch creatives (${res.status})`)
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? data
    return results.map((r) => mapCreative(r))
  },

  getByOrder: async (orderId: string): Promise<Creative[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      order: orderId,
      includeActive: 'true',
      includeInactive: 'true',
      pageSize: '100',
      page: '1',
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/creatives?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch creatives (${res.status})`)
    const data = await res.json()
    const results: Record<string, unknown>[] = data.results ?? data
    return results.map((r) => mapCreative(r))
  },

  getById: async (id: string): Promise<Creative> => {
    const res = await apiFetch(`${API_BASE}/creatives/${id}?context=${getContext()}`, { headers: JSON_HEADERS })
    if (!res.ok) throw new Error(`Failed to fetch creative (${res.status})`)
    return mapCreative(await res.json())
  },

  update: async (id: string, input: CreativeInput): Promise<Creative> => {
    const res = await apiFetch(`${API_BASE}/creatives/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, `Failed to save creative (${res.status})`)
    return mapCreative(await res.json())
  },

  /**
   * Saves several creatives in one request. Each payload carries only the
   * fields it changes; the server leaves the rest alone and applies the whole
   * list in one transaction.
   */
  updateMany: async (inputs: CreativeBulkInput[]): Promise<Creative[]> => {
    const res = await apiFetch(`${API_BASE}/creatives?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(inputs),
    })
    if (!res.ok) throw new Error(`Failed to save creatives (${res.status})`)
    return toArray(await res.json()).map((r) => mapCreative(r))
  },

  /**
   * Creates several creatives at client-chosen ids in one request. The server
   * applies the list in one transaction, so either every creative is made or
   * none is.
   */
  createMany: async (inputs: (CreativeInput & { id: string })[]): Promise<Creative[]> => {
    const res = await apiFetch(`${API_BASE}/creatives?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(inputs),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create creatives')
    return toArray(await res.json()).map((r) => mapCreative(r))
  },

  /**
   * Reads the tags out of a third-party trafficking sheet (Excel, CSV or the
   * text export of an ad server). Nothing is saved — the server only parses.
   */
  parseThirdPartyFile: async (file: File): Promise<ThirdPartyTagRow[]> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await apiFetch(`${API_BASE}/bulkcreatives/thirdparty?context=${getContext()}`, {
      method: 'POST',
      body: formData,
    })
    if (!res.ok) await throwApiError(res, 'Failed to read the tag file')
    const orders = toArray<Record<string, unknown>>(await res.json())
    return orders.flatMap((order) =>
      ((order.creatives as Record<string, unknown>[]) ?? [])
        .filter((c) => typeof c.tag === 'string' && c.tag)
        .map((c) => ({
          name: (c.creativeName as string) || '',
          tag: c.tag as string,
          width: (c.width as number) ?? undefined,
          height: (c.height as number) ?? undefined,
        }))
    )
  },
}
